// =============================================================================
// VandieZ admin-web -- Vehicle transfer approvals (/vehicle-transfers/pending)
// =============================================================================
// The VT-08 work queue. `initiate_transfer` parks a handover in
// `admin_approval_required` when the incoming driver already has an
// active vehicle, and stops. Until this page shipped the resulting rows
// were reachable only by curl: the party-scoped GET routes hide them
// from admins, so nobody who could act on one could see one.
//
// WHY QUEUE AGE IS THE HEADLINE COLUMN
// ------------------------------------
// `ix_vehicle_transfers_one_live_per_vehicle` (migration 0060) counts
// `admin_approval_required` as live, so a parked row occupies its
// vehicle's ONLY live-transfer slot. Nobody can start a fresh transfer
// for that truck until an admin approves or rejects this one. Age is
// therefore not cosmetic -- it is how long a truck has been stuck, which
// is why the endpoint sorts oldest-first and why this page renders the
// API's order verbatim (see `load` below: no client-side re-sorting).
//
// THE CODE IS SHOWN ONCE
// ----------------------
// Approve mints the plaintext handover code and returns it in the
// response body, to the approving admin, exactly once (decision D-10).
// No GET can produce it again -- the read model has no field for it and
// the DB holds a hash. So the code panel below is a STATE BRANCH inside
// the decision modal, never a route: a route taking the code as an
// argument would render correctly once and be broken on every later
// visit (back button, restored history, a shared link). As a state
// branch the code's lifetime is exactly the modal's lifetime. It is
// never written to localStorage, sessionStorage, the URL, a toast body
// or the clipboard. This mirrors VehicleTransferInitiateScreen in the
// driver app, which solves the same constraint the same way.
// =============================================================================

import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Truck,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { vehicleTransfersAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import {
  blockingVehicleIdOf,
  type AdminParkedTransferRow,
} from "@/types/vehicle_transfers";

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Queue age -- the number this page exists to surface
// ---------------------------------------------------------------------------
interface QueueAge {
  label: string;
  /** Escalating tint. A truck stuck for days is a different problem from
   *  one parked an hour ago, and the operator should see which is which
   *  without reading timestamps. */
  tone: "muted" | "warning" | "danger";
}

function queueAge(createdAtIso: string, now: Date = new Date()): QueueAge {
  const created = new Date(createdAtIso);
  if (Number.isNaN(created.getTime())) {
    return { label: createdAtIso, tone: "muted" };
  }
  const minutes = Math.max(0, Math.round((now.getTime() - created.getTime()) / 60000));
  if (minutes < 60) {
    return { label: `${minutes} min`, tone: "muted" };
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return { label: `${hours} h`, tone: hours >= 12 ? "warning" : "muted" };
  }
  const days = Math.round(hours / 24);
  return { label: `${days} d`, tone: days >= 3 ? "danger" : "warning" };
}

const AGE_TONE: Record<QueueAge["tone"], string> = {
  muted: "bg-muted text-muted-foreground",
  warning: "bg-warning-subtle text-warning-subtle-foreground",
  danger: "bg-danger-subtle text-danger-subtle-foreground",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ---------------------------------------------------------------------------
// Blocker cell -- FSD p101's "conflict view"
// ---------------------------------------------------------------------------
// p94 lists seven conditions that park a transfer and migration 0068
// implements five, so this cell can no longer be a single boolean. A
// reviewer shown one of two blockers clears it, approves, and is surprised
// by the second -- which is the failure this renders against.
//
// Labels are plain language rather than the reason codes: the person
// reading this has to decide whether to override, and
// `party_not_in_good_standing` does not tell them what to go and look at.
const REASON_LABELS: Record<string, string> = {
  new_driver_has_active_vehicle: "Still has a vehicle",
  new_driver_has_trip_on_another_vehicle: "On a trip elsewhere",
  vehicle_documents_expired: "Vehicle documents expired",
  admin_initiated_override: "Admin-initiated override",
  party_not_in_good_standing: "Driver or vehicle suspended or under review",
  // VT-03. Both name the OUTGOING driver, because that is who the admin
  // must go and talk to -- every other reason here concerns the incoming
  // one, and "driver owes money" without saying which driver sends the
  // reviewer to the wrong person.
  outgoing_driver_owes_platform: "Current driver owes the platform",
  outgoing_driver_has_open_dispute:
    "Current driver has an open dispute on this vehicle",
};

function BlockerCell({
  row,
}: {
  row: AdminParkedTransferRow;
}): React.JSX.Element {
  const reasons = row.admin_approval_reasons;

  // Rows parked before migration 0068 carry no reasons. Falling back to the
  // live flag keeps them reviewable rather than rendering an empty cell in
  // the one column this page exists to show.
  //
  // Nullish rather than `=== null`: a backend older than 0068 omits the key
  // entirely, and `undefined.length` would take the whole queue down over a
  // column that is only advisory.
  if (reasons == null || reasons.length === 0) {
    return row.new_driver_has_active_vehicle ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-danger-subtle px-2 py-0.5 text-xs font-medium text-danger-subtle-foreground">
        <ShieldAlert className="h-3 w-3" />
        Still has a vehicle
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full bg-success-subtle px-2 py-0.5 text-xs font-medium text-success-subtle-foreground">
        <Check className="h-3 w-3" />
        Blocker cleared
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {reasons.map((code) => {
        // The active-vehicle condition is the only one the backend also
        // re-resolves live, so it is the only one that can have cleared
        // itself since the row parked. Saying so is the difference between
        // "go and fix this" and "this is safe to approve now".
        const cleared =
          code === "new_driver_has_active_vehicle" &&
          !row.new_driver_has_active_vehicle;
        return (
          <span
            key={code}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
              cleared
                ? "bg-success-subtle text-success-subtle-foreground"
                : "bg-danger-subtle text-danger-subtle-foreground",
            )}
          >
            {cleared ? (
              <Check className="h-3 w-3" />
            ) : (
              <ShieldAlert className="h-3 w-3" />
            )}
            {REASON_LABELS[code] ?? code}
            {cleared ? " (now cleared)" : ""}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queue page
// ---------------------------------------------------------------------------
export function VehicleTransfersPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<AdminParkedTransferRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<AdminParkedTransferRow | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await vehicleTransfersAdminApi.pendingApproval(
          accessToken,
          { limit: PAGE_SIZE, offset: 0 },
          signal,
        );
        // Rendered in the order the API returned them. The endpoint sorts
        // oldest-first precisely because these rows block their vehicles;
        // re-sorting here would bury the worst case.
        setRows(res.items);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(queueError(e));
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); };
  }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vehicle transfer approvals"
        description="Handovers parked for review, oldest first. Each one holds its vehicle's only live transfer slot — until it is approved or rejected, that truck cannot be transferred by anyone."
        actions={
          <Button variant="outline" size="sm" onClick={() => { void load(); }}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Awaiting review ({total})</CardTitle>
          <CardDescription>
            A transfer parks here when the incoming driver already has an
            active vehicle. Approving mints the handover code and shows it to
            you once — read it back to the initiator, it cannot be retrieved
            afterwards.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && rows.length === 0 ? (
            <TableSkeleton columns={6} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              Nothing waiting — no vehicle is stuck behind a review.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Vehicle transfers awaiting admin approval, oldest first
                </caption>
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Waiting</th>
                    <th className="py-2 pr-4">Vehicle</th>
                    <th className="py-2 pr-4">Handover</th>
                    <th className="py-2 pr-4">Blocker</th>
                    <th className="py-2 pr-4">Reason</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const age = queueAge(r.created_at);
                    return (
                      <tr key={r.id} className="border-b last:border-b-0 align-top">
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                              AGE_TONE[age.tone],
                            )}
                          >
                            {age.label} stuck
                          </span>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {formatDateTime(r.created_at)}
                          </div>
                        </td>
                        <td className="py-3 pr-4 font-medium">
                          <Link
                            to={`/vehicles/${r.vehicle_id}`}
                            className="inline-flex items-center gap-1 hover:underline"
                          >
                            <Truck className="h-3.5 w-3.5 shrink-0" />
                            {r.vehicle_registration_number ?? r.vehicle_id}
                          </Link>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap items-center gap-1">
                            <Link
                              to={`/users/${r.old_driver_id}`}
                              className="hover:underline"
                            >
                              {r.old_driver_name ?? "Unknown driver"}
                            </Link>
                            <ArrowRight
                              className="h-3.5 w-3.5 text-muted-foreground"
                              aria-label="to"
                            />
                            <Link
                              to={`/users/${r.new_driver_id}`}
                              className="hover:underline"
                            >
                              {r.new_driver_name ?? "Unknown driver"}
                            </Link>
                          </div>
                          {r.new_driver_phone !== null && (
                            <div className="mt-1 font-mono text-xs text-muted-foreground">
                              {r.new_driver_phone}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <BlockerCell row={r} />
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground max-w-[16rem]">
                          {r.reason ?? "—"}
                        </td>
                        <td className="py-3 pr-4">
                          <Button size="sm" onClick={() => { setReviewing(r); }}>
                            Review
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {reviewing !== null && (
        <TransferDecisionModal
          row={reviewing}
          onClose={() => { setReviewing(null); }}
          onSettled={() => {
            setReviewing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error mapping
// ---------------------------------------------------------------------------
function queueError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) {
      return "You don't have the vehicle.transfer.approve permission. Ask a super admin to grant it.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

/**
 * Turn a decision failure into something the operator can act on.
 *
 * The 409s here are not bugs — they are the endpoint refusing to produce
 * a transfer that would fail later — so each one gets the specific next
 * step rather than a generic "request failed".
 */
function decisionError(e: unknown, row: AdminParkedTransferRow): {
  message: string;
  blockingVehicleId: string | null;
} {
  if (e instanceof ApiError) {
    if (e.code === "NEW_DRIVER_STILL_HAS_ACTIVE_VEHICLE") {
      const blocking = blockingVehicleIdOf(e);
      const who = row.new_driver_name ?? "The incoming driver";
      return {
        message:
          `${who} still holds an active vehicle, so this handover cannot be ` +
          "armed: a driver may hold only one at a time, and accepting would " +
          "bounce the transfer straight back into this queue. Transfer or " +
          "unpair their current vehicle first, then approve — or reject this " +
          "transfer to free the truck.",
        blockingVehicleId: blocking,
      };
    }
    if (e.code === "TRANSFER_NOT_PARKED") {
      return {
        message:
          "Somebody already decided this transfer, or the initiator cancelled " +
          "it. Refresh the queue to see where it landed.",
        blockingVehicleId: null,
      };
    }
    if (e.code === "VEHICLE_HAS_ACTIVE_TRIP") {
      return {
        message:
          "This vehicle is on a trip right now. Wait for the trip to complete " +
          "or cancel it, then approve.",
        blockingVehicleId: null,
      };
    }
    if (e.code === "VEHICLE_HAS_NO_ACTIVE_DRIVER") {
      return {
        message:
          "The outgoing pairing has gone away while this sat in the queue, so " +
          "there is nothing left to hand over. Reject this row.",
        blockingVehicleId: null,
      };
    }
    if (e.code === "DRIVER_NOT_KYC_VERIFIED") {
      return {
        message:
          "The incoming driver is no longer KYC-eligible to receive a vehicle. " +
          "Clear their KYC first, or reject this transfer.",
        blockingVehicleId: null,
      };
    }
    if (e.status === 403) {
      return {
        message:
          "You don't have the vehicle.transfer.approve permission. Ask a super admin.",
        blockingVehicleId: null,
      };
    }
    if (e.status === 404) {
      return {
        message: "This transfer no longer exists. Refresh the queue.",
        blockingVehicleId: null,
      };
    }
    return { message: e.problem.detail, blockingVehicleId: null };
  }
  return { message: "Unexpected error.", blockingVehicleId: null };
}

// ---------------------------------------------------------------------------
// Decision modal: review → (approve → code panel) | reject
// ---------------------------------------------------------------------------
interface TransferDecisionModalProps {
  row: AdminParkedTransferRow;
  onClose: () => void;
  /** Called once the operator is finished, so the queue can refetch. */
  onSettled: () => void;
}

function TransferDecisionModal({
  row,
  onClose,
  onSettled,
}: TransferDecisionModalProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<"approve" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blockingVehicleId, setBlockingVehicleId] = useState<string | null>(null);

  // THE plaintext code, for as long as this component is mounted and no
  // longer. Never written to storage, the URL, or a toast body; there is
  // no clipboard button on purpose (the clipboard is a shared,
  // cross-app, sometimes-synced surface). Setting it also switches the
  // modal to its second branch — that branch is not a route, so it
  // cannot be reached again by navigating.
  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);

  const showingCode = secretCode !== null;

  // Escape closes — except while the code is on screen, where there is no
  // safe accidental dismissal: closing destroys the only copy, so the
  // operator has to say so explicitly.
  useEffect(() => {
    if (showingCode) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [onClose, showingCode]);

  const notesFilled = notes.trim().length > 0;
  const busy = submitting !== null;

  const approve = async (): Promise<void> => {
    if (accessToken === null) {
      return;
    }
    setSubmitting("approve");
    setError(null);
    setBlockingVehicleId(null);
    try {
      const res = await vehicleTransfersAdminApi.approve(accessToken, row.id, {
        ...(notesFilled ? { notes: notes.trim() } : {}),
      });
      // The body carries the only copy of the code. Toast deliberately
      // does not repeat it — a toast lingers on screen after the modal
      // is gone and is exactly the kind of surface D-10 keeps it off.
      toast.success("Transfer approved.");
      setCodeExpiresAt(res.code_expires_at);
      setSecretCode(res.secret_code);
    } catch (e: unknown) {
      const mapped = decisionError(e, row);
      setError(mapped.message);
      setBlockingVehicleId(mapped.blockingVehicleId);
    } finally {
      setSubmitting(null);
    }
  };

  const reject = async (): Promise<void> => {
    if (accessToken === null || !notesFilled) {
      return;
    }
    setSubmitting("reject");
    setError(null);
    setBlockingVehicleId(null);
    try {
      await vehicleTransfersAdminApi.reject(accessToken, row.id, {
        notes: notes.trim(),
      });
      toast.success("Transfer rejected — the vehicle is free to transfer again.");
      onSettled();
    } catch (e: unknown) {
      const mapped = decisionError(e, row);
      setError(mapped.message);
      setBlockingVehicleId(mapped.blockingVehicleId);
    } finally {
      setSubmitting(null);
    }
  };

  const leaveCodePanel = (): void => {
    // Drop the code before navigating away so a re-render on the way out
    // cannot paint it again after the operator said they were done.
    setSecretCode(null);
    onSettled();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-decision-title"
    >
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {showingCode ? (
          <CodePanel
            code={secretCode}
            codeExpiresAt={codeExpiresAt}
            onDone={leaveCodePanel}
          />
        ) : (
          <>
            <CardHeader>
              <CardTitle id="transfer-decision-title">
                Review vehicle transfer
              </CardTitle>
              <CardDescription>
                Approving mints the handover code and shows it to you once, to
                read back to the initiator. Rejecting frees the vehicle and
                leaves the current driver paired.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <dl className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Vehicle</dt>
                  <dd className="font-medium">
                    {row.vehicle_registration_number ?? row.vehicle_id}
                  </dd>
                </div>
                <div className="mt-1 flex justify-between gap-2">
                  <dt className="text-muted-foreground">Current driver</dt>
                  <dd>{row.old_driver_name ?? "—"}</dd>
                </div>
                <div className="mt-1 flex justify-between gap-2">
                  <dt className="text-muted-foreground">Incoming driver</dt>
                  <dd>
                    {row.new_driver_name ?? "—"}
                    {row.new_driver_phone !== null && (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {row.new_driver_phone}
                      </span>
                    )}
                  </dd>
                </div>
                <div className="mt-1 flex justify-between gap-2">
                  <dt className="text-muted-foreground">Parked since</dt>
                  <dd>{formatDateTime(row.created_at)}</dd>
                </div>
              </dl>

              {row.new_driver_has_active_vehicle && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    The incoming driver still has an active vehicle, so approve
                    will be refused. Resolve their current vehicle first, or
                    reject this transfer.
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <Label htmlFor="transfer-decision-notes">
                  Notes (required to reject)
                </Label>
                <Textarea
                  id="transfer-decision-notes"
                  value={notes}
                  onChange={(e) => { setNotes(e.target.value); }}
                  maxLength={1000}
                  disabled={busy}
                  placeholder="Why this override is justified, or why the handover is being killed."
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  The initiator will ask why a rejected transfer was killed, so
                  the note is mandatory there. Never paste the handover code
                  here — notes are stored and shown to other admins.
                </p>
              </div>

              {error !== null && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                    {blockingVehicleId !== null && (
                      <span className="mt-2 block">
                        Blocking vehicle:{" "}
                        <Link
                          to={`/vehicles/${blockingVehicleId}`}
                          className="font-mono underline"
                        >
                          {blockingVehicleId}
                        </Link>
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void reject()}
                  disabled={busy || !notesFilled}
                  title={
                    notesFilled
                      ? undefined
                      : "Add a note explaining the rejection."
                  }
                >
                  {submitting === "reject" ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      Rejecting…
                    </>
                  ) : (
                    <>
                      <XCircle className="mr-1 h-4 w-4" /> Reject
                    </>
                  )}
                </Button>
                <Button onClick={() => void approve()} disabled={busy}>
                  {submitting === "approve" ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      Approving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The one and only rendering of a plaintext handover code
// ---------------------------------------------------------------------------
function CodePanel({
  code,
  codeExpiresAt,
  onDone,
}: {
  code: string | null;
  codeExpiresAt: string | null;
  onDone: () => void;
}): React.JSX.Element {
  return (
    <>
      <CardHeader>
        <CardTitle id="transfer-decision-title">Handover code</CardTitle>
        <CardDescription>
          Read this back to the initiator now. They relay it to the incoming
          driver face to face — it is never sent to either of them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {code === null ? (
          // Defensive: the approve path always mints a code, so this is a
          // backend contract change rather than a state we can produce.
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The transfer was approved but no code came back. Reject and
              re-initiate it — the handover cannot be completed without one.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="rounded-md bg-primary-subtle px-4 py-6 text-center">
              <div
                className="font-mono text-4xl font-bold tracking-[0.4em] text-primary-subtle-foreground"
                // Spelled out for screen readers: "K7PQ4" would otherwise be
                // announced as a word, useless for something read aloud
                // character by character.
                aria-label={`Handover code: ${code.split("").join(" ")}`}
              >
                {code}
              </div>
              <p className="mt-3 text-xs text-primary-subtle-foreground">
                Never stored in plaintext. The server keeps only a hash.
              </p>
            </div>

            <div className="flex items-start gap-2 rounded-md bg-warning-subtle px-3 py-2 text-sm text-warning-subtle-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="font-medium">
                This code is shown once. Closing this dialog destroys it — no
                screen, export or API call can show it again.
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              {codeExpiresAt === null
                ? "No expiry was returned for this code."
                : `The code stops working at ${formatDateTime(codeExpiresAt)}.`}
            </p>

            <div className="rounded-md border px-3 py-2 text-sm">
              <div className="font-medium">If the code is lost</div>
              <p className="mt-1 text-xs text-muted-foreground">
                There is no resend. The transfer must be rejected and
                re-initiated from scratch, which mints a fresh code.
              </p>
            </div>
          </>
        )}

        <div className="flex justify-end">
          <Button onClick={onDone}>
            <Check className="mr-1 h-4 w-4" /> I have shared this code
          </Button>
        </div>
      </CardContent>
    </>
  );
}
